/**
 * Batch 13 (PRD defect 7 Oct 2026): after a front-end deploy, a tab opened on the old build asked for a lazy screen whose
 * file name changed ("Failed to fetch dynamically imported module …/assets/SearchPage-CKiaUXk8.js") and showed the raw
 * router error. A stale chunk is fixed by loading the new build: reload once, automatically; if that already happened in
 * the last minute (a real outage, not a deploy), stop and say so instead of reloading in a loop.
 */
export const RELOAD_KEY = "giraffe:stale-chunk-reload";
export const RELOAD_WINDOW_MS = 60_000;

const PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
];

/** True for the errors a browser raises when a lazily loaded file of an older build is gone. */
export function isStaleChunkError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : typeof error === "string" ? error : "";
  return PATTERNS.some((pattern) => pattern.test(text));
}

interface ReloadDeps {
  storage?: Pick<Storage, "getItem" | "setItem">;
  reload?: () => void;
  now?: () => number;
}

/**
 * Reloads the page once for a stale chunk and returns true; returns false (and does nothing) when a reload for the same
 * reason already happened within {@link RELOAD_WINDOW_MS}, so a broken deploy never loops.
 */
export function reloadOnceForStaleChunk({ storage = safeSession(), reload = () => window.location.reload(), now = Date.now }: ReloadDeps = {}): boolean {
  const last = Number(storage?.getItem(RELOAD_KEY) ?? "0");
  if (Number.isFinite(last) && last > 0 && now() - last < RELOAD_WINDOW_MS) return false;
  try {
    storage?.setItem(RELOAD_KEY, String(now()));
  } catch {
    return false; // no storage: no loop guard, so never reload automatically
  }
  reload();
  return true;
}

function safeSession(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Vite fires `vite:preloadError` when a dynamic import's preload fails; reload once instead of surfacing the error. */
export function installStaleChunkReload(target: Window = window): void {
  target.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}
